/**
 * GroupResultsView
 *
 * Results reveal and final decision view for Group Prioritization.
 *
 * REVEALING mode: options animate in lowest-rank-first, building to the winner.
 * DECIDED mode: shows final ranked list with winner highlighted; host has already chosen.
 *
 * Host controls (if isHost and not decided): "Choose this" per-option CTA.
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useGroupSessionStore } from '../../stores/prioritization/groupSessionStore';
import type { GroupOptionResult, GroupCriterionResult } from '../../types/prioritization/groupSession';
import { PreS6ResultsPanel } from './PreS6ResultsPanel';
import { ReverseActionButton } from '../reversals/ReverseActionButton';
import { reopenGroupDecision } from '../../lib/reversals/reversalsService';

// ─── Props ────────────────────────────────────────────────────────────────────

interface GroupResultsViewProps {
  sessionId: string;
  isHost: boolean;
  revealMode: boolean;
}

// ─── Score bar ────────────────────────────────────────────────────────────────

function ScoreBar({ score, max = 5 }: { score: number; max?: number }) {
  const pct = Math.min(100, (score / max) * 100);
  return (
    <div
      style={{
        height: '6px',
        background: 'var(--rule-bright)',
        borderRadius: '3px',
        overflow: 'hidden',
        flex: 1,
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${pct}%`,
          background: 'var(--mustard)',
          borderRadius: '3px',
          transition: 'width 0.6s ease',
        }}
      />
    </div>
  );
}

// ─── Criterion breakdown row ──────────────────────────────────────────────────

function CriterionBreakdownRow({
  cr,
  divergenceThreshold,
}: {
  cr: GroupCriterionResult;
  divergenceThreshold: number;
}) {
  const isDivergent = cr.stdDev > divergenceThreshold;

  return (
    <div
      style={{
        marginBottom: '10px',
        paddingBottom: '10px',
        borderBottom: '1px solid var(--rule)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '6px',
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--sans)',
            fontSize: '13px',
            color: 'var(--text-sec)',
            flex: 1,
          }}
        >
          {cr.criteriaTitle}
        </span>
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: '11px',
            color: 'var(--text-ter)',
          }}
        >
          wt {cr.weight}
        </span>
        {isDivergent && (
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: '10px',
              color: 'var(--coral)',
              background: 'var(--coral-dim)',
              borderRadius: '4px',
              padding: '2px 6px',
            }}
          >
            ↕ divergent
          </span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <ScoreBar score={cr.mean} />
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: '12px',
            color: 'var(--text-pri)',
            whiteSpace: 'nowrap',
          }}
        >
          {cr.mean.toFixed(1)} / 5
        </span>
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: '11px',
            color: isDivergent ? 'var(--coral)' : 'var(--text-ter)',
            whiteSpace: 'nowrap',
          }}
        >
          σ {cr.stdDev.toFixed(2)}
        </span>
      </div>
      {/* Per-member scores */}
      {cr.memberScores.length > 0 && (
        <div
          style={{
            marginTop: '8px',
            display: 'flex',
            gap: '6px',
            flexWrap: 'wrap',
          }}
        >
          {cr.memberScores.map((ms) => (
            <span
              key={ms.userId}
              style={{
                fontFamily: 'var(--mono)',
                fontSize: '11px',
                color: 'var(--text-sec)',
                background: 'var(--rule)',
                borderRadius: '4px',
                padding: '2px 6px',
              }}
              title={ms.displayName ?? 'Participant'}
            >
              {(ms.displayName ?? 'Participant').split(' ')[0]}: {ms.score}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Member participation grid ────────────────────────────────────────────────

function ParticipationGrid({ participation }: { participation: GroupOptionResult['memberParticipation'] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {participation.map((mp) => {
        const color =
          mp.status === 'complete'
            ? 'var(--green)'
            : mp.status === 'partial'
            ? 'var(--mustard)'
            : 'var(--text-ter)';
        const label =
          mp.status === 'complete' ? 'Complete' : mp.status === 'partial' ? 'Partial' : 'Not scored';
        return (
          <div
            key={mp.userId}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: color,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontFamily: 'var(--sans)',
                fontSize: '13px',
                color: 'var(--text-sec)',
                flex: 1,
              }}
            >
              {mp.displayName ?? 'Participant'}
            </span>
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: '11px',
                color,
              }}
            >
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Option Result Card ────────────────────────────────────────────────────────

function OptionCard({
  result,
  divergenceThreshold,
  isWinner,
  isDecided,
  isHost,
  visible,
  onSelectWinner,
  actionError,
}: {
  result: GroupOptionResult;
  divergenceThreshold: number;
  isWinner: boolean;
  isDecided: boolean;
  isHost: boolean;
  visible: boolean;
  onSelectWinner: (itemId: string) => void;
  /** Why picking THIS option as the winner failed (or only partly worked). */
  actionError?: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const [confirmSelect, setConfirmSelect] = useState(false);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const borderColor = isWinner
    ? 'var(--mustard)'
    : result.isDivergent
    ? 'var(--coral)'
    : 'var(--rule)';

  const bgGlow = isWinner ? 'var(--mustard-dim)' : undefined;

  const handleChoose = useCallback(() => {
    if (!confirmSelect) {
      setConfirmSelect(true);
      confirmTimerRef.current = setTimeout(() => setConfirmSelect(false), 4000);
      return;
    }
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    setConfirmSelect(false);
    onSelectWinner(result.itemId);
  }, [confirmSelect, result.itemId, onSelectWinner]);

  return (
    <div
      style={{
        borderLeft: `4px solid ${borderColor}`,
        background: bgGlow ?? 'var(--surface)',
        border: `1px solid ${borderColor}`,
        borderRadius: '10px',
        padding: '16px',
        marginBottom: '12px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(16px)',
        transition: 'opacity 0.5s ease, transform 0.5s ease',
      }}
    >
      {/* Rank + title row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          marginBottom: '12px',
        }}
      >
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: isWinner ? 'var(--mustard)' : 'var(--rule-bright)',
            color: isWinner ? '#0B0F19' : 'var(--text-sec)',
            fontFamily: 'var(--mono)',
            fontWeight: 700,
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {result.rank}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span
              style={{
                fontFamily: 'var(--serif)',
                fontSize: isWinner ? '18px' : '15px',
                fontWeight: isWinner ? 700 : 600,
                color: isWinner ? 'var(--mustard)' : 'var(--text-pri)',
              }}
            >
              {result.title}
            </span>
            {result.isDivergent && !isWinner && (
              <span
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: '11px',
                  color: 'var(--coral)',
                  background: 'var(--coral-dim)',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  flexShrink: 0,
                }}
              >
                Some disagreement
              </span>
            )}
            {isWinner && isDecided && (
              <span
                className="dark:!text-[var(--text-ter)]"
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: '11px',
                  color: 'var(--green)',
                  background: 'var(--green-dim)',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  flexShrink: 0,
                }}
              >
                Group choice
              </span>
            )}
          </div>
          {/* Score + bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginTop: '6px',
            }}
          >
            <ScoreBar score={result.weightedScore} />
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: '13px',
                color: isWinner ? 'var(--mustard)' : 'var(--text-sec)',
                whiteSpace: 'nowrap',
                fontWeight: isWinner ? 700 : 400,
              }}
            >
              {result.weightedScore.toFixed(2)} / 5
            </span>
          </div>
        </div>
      </div>

      {/* Expand / collapse toggle */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--text-ter)',
          fontFamily: 'var(--sans)',
          fontSize: '12px',
          cursor: 'pointer',
          padding: '0',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        {expanded ? '▲ Hide detail' : '▼ Show breakdown'}
      </button>

      {/* Expandable detail */}
      {expanded && (
        <div style={{ marginTop: '16px' }}>
          <div
            style={{
              fontFamily: 'var(--mono)',
              fontSize: '10px',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-ter)',
              marginBottom: '10px',
            }}
          >
            Criteria Breakdown
          </div>
          {result.criteriaBreakdown.map((cr) => (
            <CriterionBreakdownRow
              key={cr.criteriaId}
              cr={cr}
              divergenceThreshold={divergenceThreshold}
            />
          ))}

          <div
            style={{
              fontFamily: 'var(--mono)',
              fontSize: '10px',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-ter)',
              margin: '14px 0 10px',
            }}
          >
            Participation
          </div>
          <ParticipationGrid participation={result.memberParticipation} />
        </div>
      )}

      {/* Host choose button */}
      {isHost && !isDecided && (
        <div style={{ marginTop: '14px' }}>
          <button
            type="button"
            onClick={handleChoose}
            style={{
              background: confirmSelect ? 'var(--coral)' : 'transparent',
              border: `1px solid ${confirmSelect ? 'var(--coral)' : 'var(--rule-bright)'}`,
              borderRadius: '8px',
              color: confirmSelect ? '#fff' : 'var(--text-sec)',
              fontFamily: 'var(--sans)',
              fontSize: '13px',
              fontWeight: confirmSelect ? 700 : 400,
              padding: '8px 16px',
              cursor: 'pointer',
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            {confirmSelect ? 'Confirm group recommendation?' : 'Set as group recommendation'}
          </button>
        </div>
      )}
      {actionError && (
        <p
          role="alert"
          style={{
            marginTop: '10px',
            fontFamily: 'var(--sans)',
            fontSize: '13px',
            color: 'var(--coral)',
            lineHeight: 1.5,
          }}
        >
          {actionError}
        </p>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function GroupResultsView({ sessionId, isHost, revealMode }: GroupResultsViewProps) {
  const {
    currentSession,
    results,
    revealAnimating,
    allScores,
    selectWinner,
    selectWinnerError,
    loadGroupSession,
  } = useGroupSessionStore();
  // Which option the host last tried to pick, so a refusal shows on that card.
  const [pickedItemId, setPickedItemId] = useState<string | null>(null);

  // Track which option indices are visible (for staggered animation)
  const [visibleIndices, setVisibleIndices] = useState<Set<number>>(new Set());
  const [buildingPhase, setBuildingPhase] = useState(true);
  const revealTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const isDecided = currentSession?.groupSessionStatus === 'DECIDED';
  const isRevealing = currentSession?.groupSessionStatus === 'REVEALING';
  const divergenceThreshold = currentSession?.divergenceThreshold ?? 1.0;

  const rankedOptions = useMemo(() => results?.rankedOptions ?? [], [results]);

  // Sort lowest rank first for reveal order (rank N … rank 1)
  const revealOrder = useMemo(() => {
    if (!rankedOptions.length) return [];
    return [...rankedOptions].reverse(); // index 0 = lowest rank, last = rank 1
  }, [rankedOptions]);

  // When in reveal mode, stagger animations
  useEffect(() => {
    if (!revealMode && !revealAnimating) {
      // Show all immediately
      setVisibleIndices(new Set(rankedOptions.map((_, i) => i)));
      setBuildingPhase(false);
      return;
    }

    setVisibleIndices(new Set());
    setBuildingPhase(true);

    revealTimersRef.current.forEach(clearTimeout);
    revealTimersRef.current = [];

    revealOrder.forEach((_, loopIdx) => {
      // Map revealOrder index back to rankedOptions index
      const optionResultAtRevealPos = revealOrder[loopIdx];
      const rankedIdx = rankedOptions.findIndex(
        (o) => o.itemId === optionResultAtRevealPos.itemId,
      );

      const t = setTimeout(() => {
        setVisibleIndices((prev) => new Set([...prev, rankedIdx]));
        // After last item reveals, end building phase
        if (loopIdx === revealOrder.length - 1) {
          setTimeout(() => setBuildingPhase(false), 800);
        }
      }, 600 * (loopIdx + 1));

      revealTimersRef.current.push(t);
    });

    return () => {
      revealTimersRef.current.forEach(clearTimeout);
    };
    // Keyed on the option COUNT on purpose: a refreshed results object with the
    // same options must not restart the reveal animation mid-way.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, intentional (see above)
  }, [revealMode, revealAnimating, rankedOptions.length]);

  const handleSelectWinner = useCallback(
    (itemId: string) => {
      setPickedItemId(itemId);
      void selectWinner(sessionId, itemId);
    },
    [sessionId, selectWinner],
  );

  // ── Empty state ────────────────────────────────────────────────────────────
  if (rankedOptions.length === 0) {
    return (
      <div
        style={{
          padding: '60px 0',
          textAlign: 'center',
          fontFamily: 'var(--sans)',
          fontSize: '14px',
          color: 'var(--text-ter)',
        }}
      >
        {allScores.length === 0
          ? 'No scores submitted yet. Results will appear here once members score.'
          : 'Computing results…'}
      </div>
    );
  }

  const winnerItemId = rankedOptions.find((o) => o.rank === 1)?.itemId;

  return (
    <>
      <style>{`
        @keyframes pp-fade-slide-in {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: none; }
        }
      `}</style>

      <div>
        {/* ── Building to winner banner ─────────────────────────────────────── */}
        {buildingPhase && (revealMode || revealAnimating) && (
          <div
            style={{
              textAlign: 'center',
              marginBottom: '20px',
              padding: '12px',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--serif)',
                fontSize: '16px',
                color: 'var(--text-sec)',
                fontStyle: 'italic',
              }}
            >
              Building to the winner…
            </span>
          </div>
        )}

        {/* ── Group Recommendation banner ───────────────────────────────────── */}
        {isDecided && (
          <div
            style={{
              background: 'var(--mustard-dim)',
              border: '1px solid var(--mustard)',
              borderRadius: '10px',
              padding: '14px 18px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
            }}
          >
            <span style={{ fontSize: '22px' }}>⬆</span>
            <div>
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: '10px',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--mustard)',
                  marginBottom: '4px',
                }}
              >
                Group Recommendation
              </div>
              <div
                style={{
                  fontFamily: 'var(--serif)',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: 'var(--mustard)',
                }}
              >
                Post-S6 Results
              </div>
              <div
                style={{
                  fontFamily: 'var(--sans)',
                  fontSize: '13px',
                  color: 'var(--text-sec)',
                  lineHeight: 1.5,
                  marginTop: '4px',
                }}
              >
                This is the ranking produced by collective judgment. It is not yet the final
                organizational decision — that requires leadership review.
              </div>
              {results?.generatedAt && (
                <div
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: '11px',
                    color: 'var(--text-ter)',
                    marginTop: '6px',
                  }}
                >
                  Completed{' '}
                  {new Date(results.generatedAt).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Undo (migration 750) — host only; the server decides ───────────── */}
        {isHost && !buildingPhase && (isDecided || isRevealing) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <ReverseActionButton
              label={isDecided ? 'Undo the recommendation' : 'Undo the reveal'}
              confirmTitle={isDecided ? 'Undo the group recommendation' : 'Undo the reveal'}
              explain={
                isDecided
                  ? 'The chosen option stops being the group recommendation and the results go back to the reveal, so you can choose again. Everyone in the session sees the change.'
                  : 'The results are hidden again and scoring reopens, so members can change their scores. Everyone in the session sees the change.'
              }
              run={(reason) => reopenGroupDecision(sessionId, reason)}
              onDone={() => {
                void loadGroupSession(sessionId);
              }}
            />
          </div>
        )}

        {/* ── Pre-S6 System Recommendation (reference panel) ───────────────── */}
        {currentSession?.preS6Snapshot && (
          <PreS6ResultsPanel
            snapshot={currentSession.preS6Snapshot}
            postS6Results={isDecided ? rankedOptions : undefined}
            defaultExpanded={false}
          />
        )}

        {/* ── Divergence summary ─────────────────────────────────────────────── */}
        {!buildingPhase && rankedOptions.some((o) => o.isDivergent) && (
          <div
            style={{
              background: 'var(--coral-dim)',
              border: '1px solid var(--coral)',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              fontFamily: 'var(--sans)',
              fontSize: '13px',
              color: 'var(--coral)',
              lineHeight: 1.5,
            }}
          >
            <strong>Some options show significant disagreement</strong> — expand them to see where
            members diverged. This may be worth discussing before deciding.
          </div>
        )}

        {/* ── Ranked list ────────────────────────────────────────────────────── */}
        <div>
          {rankedOptions.map((result, idx) => (
            <OptionCard
              key={result.itemId}
              result={result}
              divergenceThreshold={divergenceThreshold}
              isWinner={result.itemId === winnerItemId}
              isDecided={isDecided}
              isHost={isHost}
              visible={visibleIndices.has(idx)}
              onSelectWinner={handleSelectWinner}
              actionError={result.itemId === pickedItemId ? selectWinnerError : null}
            />
          ))}
        </div>

        {/* ── Stats footer ────────────────────────────────────────────────────── */}
        {results && (
          <div
            style={{
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid var(--rule)',
              display: 'flex',
              gap: '20px',
              flexWrap: 'wrap',
            }}
          >
            <Stat label="Total members" value={String(results.totalMembers)} />
            <Stat label="Options scored" value={String(rankedOptions.length)} />
            <Stat
              label="Divergence threshold"
              value={`σ > ${results.divergenceThreshold.toFixed(1)}`}
            />
          </div>
        )}
      </div>
    </>
  );
}

// ─── Stat helper ─────────────────────────────────────────────────────────────

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: '10px',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--text-ter)',
          marginBottom: '2px',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: '14px',
          fontWeight: 600,
          color: 'var(--text-sec)',
        }}
      >
        {value}
      </div>
    </div>
  );
}
